import { useMemo, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useRouter } from 'next/router'
import { getProductCartItems } from '@helpers/products'
import addToCart from '@store/redux/actions/addToCart'
import { openCart, showBanner } from '@store/redux/actions/otherActions'
import { ProductVariant, ProductWithVariants } from 'types/product'
import { SubmitButtonHandlers } from '@components/ui/Button/SubmitButton'
import { createCartItem, isCartItemOutOfStock } from '@helpers/cart'
import { FilterOption } from 'types/filters'
import { CartLineItem } from 'types/checkout'
import { MTO_BUNDLE_FLAG } from '@consts/index'
import { useBundleContext } from './BundleContext'
import { scrollToTop } from '@helpers/utils'

type Tab = {
  product_type: string
  tab_title: string
  products?: ProductWithVariants[]
}

type Props = {
  isMtoBundle: boolean
  selectedProduct: ProductWithVariants
  selectedVariant: ProductVariant | undefined
  selectedFilterOptions: FilterOption[]
  tabs: Tab[]
  cartLines: CartLineItem[]
}

type BundlePiece = {
  product: ProductWithVariants
  selectedOptions: FilterOption[]
}

function getMissingBundleTypes(
  bundleTypes: string[],
  selectedPieces: Record<string, BundlePiece>
) {
  return bundleTypes.filter(type => !selectedPieces[type])
}

function getBundleCtaLabel({
  isMtoBundle,
  currentType,
  selectedVariant,
  bundleTypes,
  selectedPieces,
  tabTitleByType
}: {
  isMtoBundle: boolean
  currentType: string
  selectedVariant?: ProductVariant
  bundleTypes: string[]
  selectedPieces: Record<string, BundlePiece>
  tabTitleByType: Record<string, string>
}) {
  if (!isMtoBundle) return 'Add to basket'

  const currentTitle = tabTitleByType[currentType] || currentType

  if (!selectedVariant?.id) {
    return `Select ${currentTitle.toLowerCase()} size`
  }

  const updatedPieces: Record<string, BundlePiece> = {
    ...selectedPieces,
    [currentType]: {
      product: {} as ProductWithVariants,
      selectedOptions: []
    }
  }

  const missing = getMissingBundleTypes(bundleTypes, updatedPieces)

  if (missing.length === 0) {
    return 'Add suit to basket'
  }

  const nextTitle = tabTitleByType[missing[0]] || missing[0]
  return `Select ${nextTitle.toLowerCase()} size`
}

export function useMtoBundle({
  isMtoBundle,
  selectedProduct,
  selectedVariant,
  selectedFilterOptions,
  tabs,
  cartLines
}: Props) {
  const router = useRouter()
  const dispatch = useDispatch()
  const { state, selectPiece, reset, setLastViewed } = useBundleContext()

  const bundleTypes = useMemo(() => tabs.map(tab => tab.product_type), [tabs])

  const tabTitleByType = useMemo(() => {
    return tabs.reduce<Record<string, string>>((acc, tab) => {
      acc[tab.product_type] = tab.tab_title
      return acc
    }, {})
  }, [tabs])

  const ctaLabel = useMemo(
    () =>
      getBundleCtaLabel({
        isMtoBundle,
        currentType: selectedProduct.product_type,
        selectedVariant,
        bundleTypes,
        selectedPieces: state.selectedPieces,
        tabTitleByType
      }),
    [
      isMtoBundle,
      selectedProduct.product_type,
      selectedVariant,
      bundleTypes,
      state.selectedPieces,
      tabTitleByType
    ]
  )

  const handleBundleClick = useCallback(
    async ({
      triggerButtonSuccess,
      triggerButtonReady,
      triggerButtonError
    }: SubmitButtonHandlers) => {
      if (!isMtoBundle) {
        triggerButtonSuccess()
        return
      }

      if (!selectedVariant?.id) {
        triggerButtonReady()
        return
      }

      const currentType = selectedProduct.product_type

      const currentPiece: BundlePiece = {
        product: selectedProduct,
        selectedOptions: selectedFilterOptions
      }

      const updatedPieces: Record<string, BundlePiece> = {
        ...state.selectedPieces,
        [currentType]: currentPiece
      }

      selectPiece(currentType, currentPiece)

      const missing = getMissingBundleTypes(bundleTypes, updatedPieces)

      /**
       * STEP 1
       * Still missing bundle pieces → redirect to next tab
       */
      if (missing.length > 0) {
        triggerButtonReady()

        const nextType = missing[0]
        const nextTab = tabs.find(tab => tab.product_type === nextType)

        if (!nextTab?.products?.length) return

        /**
         * FIRST PRIORITY: previously viewed product for that tab
         */
        const lastViewedHandle = state.lastViewedProducts[nextType]

        let targetHandle: string | undefined

        if (lastViewedHandle) {
          targetHandle = lastViewedHandle
        } else {
          /**
           * SECOND: colour match from current selection
           */
          const matchingProduct = nextTab.products.find(
            p => p.title?.toLowerCase() === selectedProduct.title?.toLowerCase()
          )

          targetHandle = matchingProduct?.handle || nextTab.products[0]?.handle

          /**
           * Only store history if none existed before
           */
          if (targetHandle) {
            setLastViewed(nextType, targetHandle)
          }
        }

        if (targetHandle) {
          router
            .push(`/products/${targetHandle}`, undefined, { shallow: true })
            .then(() => {
              const element = document.getElementById('product-tabs')

              if (element) {
                scrollToTop(element.offsetTop - 100)
              }
            })
        }

        return
      }

      /**
       * STEP 2
       * All bundle pieces selected → add to cart
       */
      const bundleId = `${Date.now()}`

      const resolvedPieces = Object.values(updatedPieces).map(piece => ({
        piece,
        ...createCartItem(piece.product, piece.selectedOptions, cartLines)
      }))

      const hasOutOfStockPiece = resolvedPieces.some(
        ({ piece, lineItemInCart, fulfillmentLocation }) =>
          isCartItemOutOfStock({
            lineItemInCart,
            fulfillmentLocation,
            cartLines,
            productId: piece.product.id
          })
      )

      if (hasOutOfStockPiece) {
        triggerButtonError()
        dispatch(
          showBanner({
            isVisible: true,
            type: 'Add to basket - Out of stock',
            theme: 'error'
          })
        )
        scrollToTop()
        setTimeout(() => dispatch(showBanner({ isVisible: false })), 10000)
        return
      }

      const cartItems = resolvedPieces.flatMap(
        ({ piece, variantId, customAttributes, fulfillmentLocation }) =>
          getProductCartItems({
            quantity: 1,
            variantId: `gid://shopify/ProductVariant/${variantId}`,
            fulfillmentLocation,
            customAttributes: [
              ...customAttributes,
              { key: MTO_BUNDLE_FLAG, value: bundleId },
              {
                key: '_MTObundleRole',
                value:
                  piece.product.product_feed.category?.toLowerCase() ===
                  'trousers'
                    ? 'secondary'
                    : 'primary'
              }
            ]
          })
      )

      await dispatch(addToCart(cartItems, 'Product Page'))

      reset()
      triggerButtonSuccess()
      dispatch(openCart())
    },
    [
      isMtoBundle,
      selectedVariant,
      selectedProduct,
      selectedFilterOptions,
      bundleTypes,
      state.selectedPieces,
      cartLines,
      tabs,
      router,
      dispatch,
      selectPiece,
      reset,
      state.lastViewedProducts
    ]
  )

  return {
    ctaLabel,
    isBundle: isMtoBundle,
    handleBundleClick
  }
}
